import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { imageUrl } from '../lib/images';

export default function VejtransportEuropa() {
  return (
    <>
      <SEO
        title="Godstransport - Stykgods, partigods eller full loads - Ring nu"
        description="Godstransport med forskellig transportmateriel og transport af forskellige godstyper. Kontakt os for tilbud på den type godstransport, som du ønsker."
        keywords="Spedition, fragt, speditionsvirksomhed, international vejtransport, international godstransport, særtransport, specialtransport, ekspres forsendelse, ekspres transport, logistik, multimodal transport, landevejstransport, godstransport vej, shipping, transportrådgivning, logistikrådgivning"
        canonicalUrl="https://fastlane.dk/godstransport-i-europa"
      />

      <PageHeader
        title="Godstransport i Europa"
        subtitle="Stykgods, partigods og full loads med lastbil, bane og skib."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Godstransport i Europa' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Main intro */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <p>
              Hos Fastlane tilbyder vi international godstransport med lastbil på vej, tog på bane eller skib på sø. Eller multimodal transport i hele Europa, som er en kombination af traditionelle transportformer.
            </p>
            <p>
              Nederst på siden vejledende{' '}
              <a href="#Transittider" className="text-sky-700 hover:text-sky-900 font-semibold underline">
                booking og transittider for godstransport på vej
              </a>{' '}
              til udvalgte lande i Europa m.m. Transittiderne er baseret på standard service og hovedstrøgene. Vi oplyser gerne forventningerne transittider til og fra yderzoner.
            </p>

            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-[#b25d19] hover:bg-[#974d12] text-white text-sm font-semibold transition-all shadow-xs"
              >
                <span>Bestil godstransport</span>
                <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Pris på godstransport */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h2 className="text-xl font-bold text-[#0f2b48]">Pris på godstransport</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              Vores målsætning er at skabe det rigtige match for godstransport mellem vores udvalgte transportører og den enkelte transportopgave. Dette bevirker at fragten med godstransport bliver udført i henhold til afstemte forventninger og til den rigtige fragtrate.{' '}
              <Link to="/godsmaengder-sendingsstoerrelser" className="text-sky-700 font-semibold underline">
                Godsmængde og sendingsstørrelse
              </Link>{' '}
              har indflydelse på planlægning og gennemførelse af godstransporten.
            </p>

            <p className="text-sm font-semibold text-slate-900 pt-2">
              Der arbejdes primært med følgende sendingsstørrelser:
            </p>
            <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
              <li>Full loads (33-34 paller)</li>
              <li>Stykgods (1-3 paller)</li>
              <li>Partigods (flere end 3 paller, men ikke full load)</li>
            </ul>

            <div className="pt-3">
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-16-rev-00-hvordan-jeg-f-r-den-rigtige-fragtpris.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs font-semibold text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-200 px-3.5 py-2 rounded-lg transition-colors gap-2"
              >
                <i className="fa-solid fa-file-pdf text-red-600 text-sm" aria-hidden="true" />
                <span>Hvordan får jeg den rigtige fragtpris? (PDF)</span>
              </a>
            </div>
          </div>

          {/* Cross Trade Link */}
          <div className="p-6 bg-sky-50/50 rounded-xl border border-sky-100 flex items-start gap-4">
            <i className="fa-solid fa-arrows-split-up-and-left text-sky-700 text-xl mt-1 shrink-0" aria-hidden="true" />
            <div>
              <h3 className="font-bold text-[#0f2b48] text-base mb-1">Cross Trade</h3>
              <p className="text-sm text-slate-600">
                Har du brug for at transportere gods mellem lande hvor hverken afsender og modtager er i Danmark kan{' '}
                <Link to="/cross-trade" className="text-sky-700 font-semibold underline">
                  cross trade
                </Link>{' '}
                være en mulighed.
              </p>
            </div>
          </div>

          {/* Godstyper til godstransport */}
          <div>
            <h2 className="text-2xl font-bold text-[#0f2b48] border-b border-slate-200 pb-3 mb-4">
              Godstyper til godstransport
            </h2>
            <p className="text-sm text-slate-600 mb-4">Hos Fastlane transporterer vi følgende godstyper:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              {[
                'Kurèrforsendelser',
                'Pakker',
                'Pallegods',
                'IBC\'er',
                'Maskingodsi',
                'Industrigods',
                'Kemikalier',
                'Fødevarer',
                'Reservedele',
                'Kølegods',
                'Transport af hele læs (full loads)',
                'Fragt af messegods',
                'ADR gods (farligt gods)',
                'Projektlast (projekttransport)'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md border border-slate-100">
                  <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Transportmateriel til godstransport */}
          <div>
            <h2 className="text-2xl font-bold text-[#0f2b48] border-b border-slate-200 pb-3 mb-4">
              Transportmateriel til godstransport
            </h2>
            <p className="text-sm text-slate-600 mb-4">Fastlane tilbyder følgende transportmateriel:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              {[
                'Gardintrailer',
                'Presseningstrailer',
                'Boxtrailer',
                'Udtrækstrailer',
                'Temperatur reguleret trailer (køletrailer)',
                '45 fod Pallebred container',
                'Nedbygget trailer',
                'Megatrailer / Jumbotrailer',
                'Åben trailer',
                '3,5 ton kassevogne',
                '7,5 tons forvogne',
                'Kranbil',
                'Medbringer truck (Moffert)',
                'Sidelaster (besima)',
                'Multimodal transport'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md border border-slate-100">
                  <i className="fa-solid fa-truck text-sky-700 text-xs" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Multimodal Transport section with image */}
          <div className="border-t border-slate-200 pt-8 space-y-6">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Multimodal Transport</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              Ønsker du komplet løsning med multimodal transport, tilbyder Fastlane transport kombineret af vej transport, søfragt, togfragt og luftfragt. På den måde får du sammensat den transportløsning, der passer præcis til dit behov. Det betyder, at der bliver sammensat hvad du har brug for af transport med lastbil, skib, tog og fly.
            </p>

            <div className="w-fit max-w-full rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100">
              <img
                src={imageUrl('https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/dfds-ficaria-seaways-unloading-gothenburg.jpg', 'width=1200,quality=85,format=auto')}
                alt="RORO skib til transport af løstrailere shortsea"
                className="max-h-[350px] w-auto max-w-full object-cover block"
              />
              <p className="w-full text-xs text-slate-500 p-3 bg-slate-50 border-t border-slate-100 text-left">
                RORO skib til transport af løstrailere shortsea
              </p>
            </div>

            <p className="text-sm text-slate-700">
              Ønsker du, at Fastlane skal stå for transporten af dit gods med en kombination af flere transportformer: lastbil, tog, skib eller fly. Med andre ord{' '}
              <Link to="/fragt-kombineret-transport" className="text-sky-700 font-semibold underline">
                multimodal transport
              </Link>
              , så er du velkommen til at sende os en mail på{' '}
              <a href="mailto:booking@fastlane.dk" className="font-semibold text-sky-700">
                booking@fastlane.dk
              </a>{' '}
              eller ringe til os på{' '}
              <a href="tel:+4576121230" className="font-semibold text-slate-900 whitespace-nowrap">
                +45 76 12 12 30
              </a>
              . Du kan også sende os en{' '}
              <Link to="/fragtrater" className="text-sky-700 font-semibold underline">
                forespørgsel
              </Link>
              .
            </p>
          </div>

          {/* Transittider table */}
          <div id="Transittider" className="border-t border-slate-200 pt-8 space-y-4">
            <h2 className="text-2xl font-bold text-[#0f2b48]">
              Transittider for Godstransport på Vej i Europa
            </h2>
            <p className="text-xs text-slate-500">
              Vejledende transittider baseret på standard service og hovedstrøg.
            </p>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-sm text-slate-700">
                <thead className="bg-[#0f2b48] text-white text-xs uppercase font-bold tracking-wider">
                  <tr>
                    <th scope="col" className="px-4 py-3">Land / Destination</th>
                    <th scope="col" className="px-4 py-3">Bestillingsdag</th>
                    <th scope="col" className="px-4 py-3">Afsendelsesdag</th>
                    <th scope="col" className="px-4 py-3">Forventet leveringsdag</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[
                    ['Danmark', 'dag 0 før 13.00', 'dag 1', 'dag 2'],
                    ['Sverige (Syd)', 'dag 0 før 13.00', 'dag 1', 'dag 2/3'],
                    ['Norge (Syd)', 'dag 0 før 13.00', 'dag 1', 'dag 2/3'],
                    ['Tyskland', 'dag 0 før 13.00', 'dag 1', 'dag 3/4'],
                    ['Holland, Belgien', 'dag 0 før 13.00', 'dag 1', 'dag 3/4'],
                    ['Frankrig', 'man før 13.00\ntors før 13.00', 'tir\nfre', 'fre/man\ntir/ons'],
                    ['Finland (Syd)', 'man før 13.00\ntors før 13.00', 'tors\nfre', 'fre/man\ntir/ons'],
                    ['UK/Irland', 'Gods i havn og input til told klar', 'ons / fre', 'tirs/ons / tors/fre'],
                    ['Spanien (Barcelona)', 'man før 13.00\ntors før 13.00', 'tir\nfre', 'fre/man\ntir/ons'],
                    ['Italien (Milan)', 'man før 13.00\ntors før 13.00', 'tors\nfre', 'fre/man\nons/tors'],
                    ['Estland, Letland, Litauen', 'man før 13.00\ntors før 13.00', 'tir\nfre', 'fre/man\ntir/ons'],
                    ['Polen (Warszawa)', 'dag 0 før 13.00', 'dag 1', 'dag 2/3'],
                    ['Ungarn, Østrig, Tjekkiet', 'man før 13.00', 'tir', 'tors/fre'],
                    ['Slovakiet, Schweiz', 'tors før 13.00', 'fre', 'tir/ons'],
                    ['Slovenien, Serbien, Bosnien', 'ons før 13.00', 'fre', 'tors/fre'],
                    ['Rusland, Ukraine, Hviderus.', 'tir før 13.00', 'fre', 'ca. 7 dage'],
                    ['Tyrkiet', 'tir før 13.00', 'tors', 'ca. 7 dage'],
                    ['Grækenland', 'ons før 13.00', 'tors', 'tors/fre']
                  ].map(([land, bestil, afsend, lev], idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{land}</td>
                      <td className="px-4 py-3 whitespace-pre-line text-xs">{bestil}</td>
                      <td className="px-4 py-3 whitespace-pre-line text-xs">{afsend}</td>
                      <td className="px-4 py-3 whitespace-pre-line text-xs font-medium text-sky-800">{lev}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
